export const CLOSING_NOTICE_MS = 2000

let products = {}
const listeners = new Set()
let productDates = {}
const dateListeners = new Set()
const timers = new Map()
let subscribed = false

// Enabled in development; in other builds run localStorage.debugAuctionClose = '1' and reload
export function isCloseDebugEnabled() {
    try {
        return (
            process.env.NODE_ENV === 'development' ||
            window.localStorage.getItem('debugAuctionClose') === '1'
        )
    } catch (error) {
        return false
    }
}

export function logClose(...args) {
    if (isCloseDebugEnabled()) console.info('[auction-close]', ...args)
}

export function normalizeProductId(value) {
    if (!['string', 'number'].includes(typeof value)) return null
    const id = String(value).trim()
    return /^\d+$/.test(id) && Number(id) > 0 ? String(Number(id)) : null
}

function publish(next) {
    products = next
    listeners.forEach((listener) => listener(products))
}

export function markProductClosed(productId, reason = 'ended', source = 'unknown') {
    const id = normalizeProductId(productId)
    if (!id) {
        console.error('[closedProducts] Unrecognized product id', productId)
        return false
    }
    if (products[id]) {
        logClose(`${source}: id ${id} already ${products[id].status}, ignored`)
        return false
    }

    logClose(`${source}: id ${id} -> closing (reason: ${reason}, listeners: ${listeners.size})`)
    publish({ ...products, [id]: { status: 'closing', reason } })
    timers.set(
        id,
        setTimeout(() => {
            timers.delete(id)
            logClose(`id ${id} -> removed after ${CLOSING_NOTICE_MS}ms`)
            publish({ ...products, [id]: { ...products[id], status: 'removed' } })
        }, CLOSING_NOTICE_MS),
    )
    return true
}

export function onAuctionEnd(productId, lot) {
    logClose('socket auctionend received', { productId, lot, type: typeof productId })
    // auctionend(productId, sublotId) closes only that sublot, not the main card
    if (lot !== undefined && lot !== null && lot !== 'master') {
        logClose(`auctionend: sublot ${lot} of ${productId}, main card untouched`)
        return
    }
    markProductClosed(productId, 'ended', 'auctionend')
}

const isTruthyFlag = (value) => value === true || value === 1 || value === '1' || value === 'true'

export function onRealClosedUpdates(data) {
    logClose('socket realclosedupdates received', data)
    if (!data || typeof data !== 'object') return

    // Line item award: pid is the sublot; close the master card only after the last sublot
    if (data.isLastLineItem !== undefined && data.isLastLineItem !== null) {
        if (!isTruthyFlag(data.isLastLineItem)) {
            logClose(`realclosedupdates: line item ${data.pid} awarded, master stays open`)
            return
        }
        const masterId = normalizeProductId(data.masterId)
        if (!masterId) {
            console.error('[realclosedupdates] Last line item without a valid masterId', data)
            return
        }
        markProductClosed(masterId, 'ended', 'realclosedupdates (last line item)')
        return
    }

    const id = [data.pid, data.id, data.product_id].map(normalizeProductId).find(Boolean)
    if (!id) {
        console.error('[realclosedupdates] Unrecognized product id', data)
        return
    }
    markProductClosed(id, 'ended', 'realclosedupdates')
}

export function onProductUpdated(data) {
    logClose('socket product_updated received', data)
    if (data?.action === 'award') {
        markProductClosed(data.productId, 'award', 'product_updated')
        return
    }
    if (data?.action === 'move_to_unsold') {
        markProductClosed(data.productId, 'ended', 'product_updated (move_to_unsold)')
        return
    }
    if (!data || !['date_added', 'date_closed'].some((key) => data[key] !== undefined)) return
    const id = normalizeProductId(data.productId)
    if (!id) {
        console.error('[product_updated] Date update without a valid productId', data)
        return
    }
    const dates = {}
    for (const key of ['date_added', 'date_closed']) {
        if (data[key] === undefined) continue
        if (
            !['string', 'number'].includes(typeof data[key]) ||
            (typeof data[key] === 'string' && !data[key].trim()) ||
            !Number.isFinite(new Date(data[key]).getTime())
        ) {
            console.error('[product_updated] Invalid auction date', {
                productId: id,
                key,
                value: data[key],
            })
            return
        }
        dates[key] = data[key]
    }
    productDates = { ...productDates, [id]: { ...productDates[id], ...dates } }
    dateListeners.forEach((listener) => listener(productDates))
    logClose(`product_updated: dates updated for ${id}`, dates)
}

function ensureSocketSubscription() {
    if (subscribed) return
    subscribed = true
    // Lazy require keeps the shared socket out of modules that never render these lists
    const { socket } = require('./socket')
    logClose('subscribing shared socket', {
        url: socket.io?.uri,
        connected: socket.connected,
        id: socket.id,
    })
    socket.on('connect', () => logClose('shared socket connected', socket.id))
    socket.on('disconnect', (reason) => logClose('shared socket disconnected', reason))
    socket.on('connect_error', (error) =>
        logClose('shared socket connect_error', error?.message || error),
    )
    socket.on('auctionend', onAuctionEnd)
    socket.on('realclosedupdates', onRealClosedUpdates)
    socket.on('product_updated', onProductUpdated)
}

export function subscribeClosedProducts(listener) {
    ensureSocketSubscription()
    listeners.add(listener)
    listener(products)
    return () => listeners.delete(listener)
}

export function subscribeProductDates(listener) {
    ensureSocketSubscription()
    dateListeners.add(listener)
    listener(productDates)
    return () => dateListeners.delete(listener)
}

export function resetClosedProducts() {
    timers.forEach(clearTimeout)
    timers.clear()
    products = {}
    listeners.forEach((listener) => listener(products))
    productDates = {}
    dateListeners.forEach((listener) => listener(productDates))
}
